import React, {useMemo} from 'react'
import {DataObjectId, Hub} from 'yougile-model'
import {TableColumn} from '@components/common/base/table'
import {ChartData} from '@components/common/base/table/types'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {ColumnTw, TwHeadTitle} from '@components/statistics/by-time/containers'
import {
  NameColumn,
  TaskNameColumn
} from '@components/statistics/by-time/task-name-column'
import {
  getColumnTime,
  getColumnTimeInterval,
  getInterval
} from '@components/sticker/my-stickers/board-stickers/sticker-settings/stopwatch/stopwatch-column-utils'
import {getTimerValue} from '@components/task-timeline/get-timer-value'

export const FirstColumnWidth = 360
export const DefaultColumnWidth = 112
export const MinColumnHeight = 292

export interface DataItem {
  title: string
  className?: string
  id: DataObjectId
  taskId: DataObjectId
  maxInterval: number
  columns: {
    [columnId: DataObjectId]: {
      title: string
      interval: number
      currentLocation: boolean
    }
  }
}

export const useTableColumns = (
  boardColumns: Hub[] | null
): TableColumn<DataItem>[] => {
  return useMemo(() => {
    if (!boardColumns) {
      return []
    }

    return [
      {
        title: <NameColumn />,
        width: FirstColumnWidth,
        className: 'pr-0 pl-4 relative items-center justify-end',
        render: data => <TaskNameColumn data={data} />
      },
      ...(boardColumns?.map(column => {
        return {
          title: (
            <Tooltip offset={[0, 10]} content={column.title} placement="bottom">
              <TwHeadTitle>{column.title}</TwHeadTitle>
            </Tooltip>
          ),
          width: DefaultColumnWidth,
          className: 'text-center text-14 justify-center',
          render: (data: DataItem) => (
            <Tooltip
              disabled={!data.columns[column.id].currentLocation}
              offset={[0, 10]}
              content="Текущее расположение"
              placement="bottom"
            >
              <ColumnTw
                $currentLocation={data.columns[column.id].currentLocation}
                $highlighted={
                  data.maxInterval === data.columns[column.id].interval
                }
              >
                {data.columns[column.id].title}
              </ColumnTw>
            </Tooltip>
          ),
          sortBy: (data: DataItem) => {
            return data.columns[column.id].interval
          }
        }
      }) || [])
    ]
  }, [boardColumns])
}

interface TableRowData {
  className?: string
  data: DataItem
}
interface TableChartData {
  tableData: TableRowData[]
  chartData: ChartData[]
}

interface ColumnsTasksIntervals {
  [id: DataObjectId]: {
    id: DataObjectId
    title: string
    percentage: number
    sum: number
    taskCount: number
    interval: number
  }
}

function generateHslColors(numColors: number): string[] {
  const colors: string[] = []

  for (let i = 0; i < numColors; i++) {
    const hue = (120 * (i / numColors)).toFixed(0)
    const saturation = '100%'
    const lightness = '45%'
    const color = `hsl(${hue}, ${saturation}, ${lightness})`
    colors.push(color)
  }

  return colors
}

export const useTableChartData = (
  columns: Hub[] | null,
  data: Hub[] | null
): TableChartData => {
  return useMemo<TableChartData>(() => {
    if (!data?.length || !columns?.length) {
      return {
        tableData: [],
        chartData: []
      }
    }

    const columnsIds = columns.map(column => column.id)
    const tasksSumIntervals: ColumnsTasksIntervals =
      columnsIds.reduce<ColumnsTasksIntervals>((acc, columnId) => {
        acc[columnId] = {
          id: columnId,
          title: '',
          percentage: 0,
          sum: 0,
          taskCount: 0,
          interval: 0
        }

        return acc
      }, {})

    // Step1. get the table data + sum tasks intervals and number of tasks inside each column -> put it inside Chart data (=tasksSumIntervals)
    const tableData = data
      .map(task => {
        const taskIntervals = getTimerValue(task)
        const taskColumnsIntervals = getColumnTimeInterval(task, taskIntervals)

        return {
          data: columnsIds.reduce<DataItem>(
            (acc, columnId) => {
              acc.columns[columnId] = {
                title: getColumnTime(columnId, taskColumnsIntervals),
                interval: 0,
                currentLocation: task.location === columnId
              }

              const columnTaskInterval = taskColumnsIntervals.find(
                el => el.column?.id === columnId
              )
              if (columnTaskInterval) {
                const {timeEnd, timeBegin} = columnTaskInterval
                const interval = timeEnd - timeBegin
                acc.columns[columnId].interval = interval
                tasksSumIntervals[columnId].sum += interval
                tasksSumIntervals[columnId].taskCount += 1

                if (interval > acc.maxInterval) {
                  acc.maxInterval = interval
                }
              }

              return acc
            },
            {
              title: task.title,
              id: task.id,
              taskId: task.id,
              columns: {},
              maxInterval: 0
            }
          )
        }
      })
      .sort((a, b) => {
        const maxA = a.data.maxInterval
        const maxB = b.data.maxInterval

        return maxB - maxA
      })
      .map((row, index) => {
        return {
          className: `h-32 ${
            index % 2 === 0
              ? 'hover:!bg-transparent'
              : 'hover:!bg-line-disabled'
          } select-none`,
          ...row
        }
      })

    // Step 2. for each report column -> count the actual intervals based on tasks intervals sum and
    // number of tasks inside each column + find max value of interval in columns
    let maxInterval = 0
    Object.keys(tasksSumIntervals).forEach((columnId: DataObjectId) => {
      const {sum, taskCount} = tasksSumIntervals[columnId]
      const interval = taskCount ? Number((sum / taskCount).toFixed(4)) : 0
      tasksSumIntervals[columnId].interval = interval
      tasksSumIntervals[columnId].title = getInterval(0, interval)
      if (interval > maxInterval) {
        maxInterval = interval
      }
    })

    // Step 3. Finalize Chart Data by adding the color gradient and percentage height
    // which is equal to currentInterval / maxInterval
    let chartData = Object.values(tasksSumIntervals)
    const sortedIntervals: number[] = chartData
      .map(column => column.interval)
      .sort((a, b) => b - a)
    const gradient = generateHslColors(chartData.length)
    chartData = chartData.map(column => {
      return {
        id: column.id,
        title: column.title,
        color:
          gradient[sortedIntervals.findIndex(item => item === column.interval)],
        percentage: Number(((column.interval / maxInterval) * 100).toFixed(4))
      }
    })

    return {
      tableData,
      chartData
    }
  }, [data, columns])
}

export const useContentWidth = (numberOfColumns: number) => {
  return useMemo(() => {
    return (numberOfColumns - 1) * DefaultColumnWidth
  }, [numberOfColumns])
}
